/**
 * 🍦 PRODUCT DETAIL MODAL
 * Beautiful product customization screen
 * Matching Gemini's gorgeous design
 * Swipe left/right to navigate between items!
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCartStore } from '../store';
import { AddReward, Toast } from './AddReward';
import styles from './ProductDetail.module.css';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  category: string;
  image_url?: string;
}

interface ProductDetailProps {
  item: MenuItem;
  items: MenuItem[];
  onClose: () => void;
  onNavigate: (item: MenuItem) => void;
}

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM002': '/softserve-swirl.png', // Tropical Twist
  'FM003': '/softserve-transchocdip.png', // Choc Dip Cone
  'FM004': '/softserve-tranvanillalicious.png', // Vanillalicious
};

const getItemImage = (itemId: string) => {
  return itemImages[itemId] || '/softserve-tran.png';
};

const TOPPINGS = [
  { id: 'choc_sprinkles', name: 'Chocolate Sprinkles', price: 150 },
  { id: 'rainbow_sprinkles', name: 'Rainbow Sprinkles', price: 150 },
  { id: 'crushed_nuts', name: 'Crushed Nuts', price: 150 },
  { id: 'hot_fudge', name: 'Hot Fudge', price: 150 },
  { id: 'caramel', name: 'Caramel Drizzle', price: 150 },
];

const funMessages = [
  'Yum! Great choice! 🍦',
  'Delicious pick! 🥭',
  'Treat yourself! ✨',
  'Nice one! 🧡',
  'Mango magic! 🍦',
  'Sweet! 🎉',
];

export function ProductDetail({ item, items, onClose, onNavigate }: ProductDetailProps) {
  const navigate = useNavigate();
  const addItem = useCartStore((state) => state.addItem);
  const cartTotal = useCartStore((state) => state.totalItems());

  const [size, setSize] = useState<'small' | 'large'>('small');
  const [cartBounce, setCartBounce] = useState(false);
  const [container, setContainer] = useState<'cone' | 'cup'>('cone');
  const [selectedToppings, setSelectedToppings] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);

  // Gamification state
  const [showReward, setShowReward] = useState(false);
  const [rewardPosition, setRewardPosition] = useState({ x: 0, y: 0 });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Swipe handling
  const touchStartX = useRef<number>(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const currentIndex = items.findIndex(i => i.id === item.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  const goToPrev = () => {
    if (hasPrev) {
      onNavigate(items[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      onNavigate(items[currentIndex + 1]);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Note: preventDefault removed - touch-action: none in CSS handles this
    e.stopPropagation();
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Note: preventDefault removed - touch-action: none in CSS handles this
    e.stopPropagation();
    const currentX = e.touches[0].clientX;
    let offset = currentX - touchStartX.current;

    // Add resistance if swiping in a direction with no more items
    if ((offset > 0 && !hasPrev) || (offset < 0 && !hasNext)) {
      offset = offset * 0.3; // Resistance effect
    }

    setDragOffset(offset);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Note: preventDefault removed - touch-action: none in CSS handles this
    e.stopPropagation();
    setIsDragging(false);

    const minSwipeDistance = 80;

    if (Math.abs(dragOffset) > minSwipeDistance) {
      if (dragOffset < 0 && hasNext) {
        goToNext(); // Swipe left = next item
      } else if (dragOffset > 0 && hasPrev) {
        goToPrev(); // Swipe right = prev item
      }
    }

    // Reset offset (bounce back)
    setDragOffset(0);
  };

  const basePrice = item.price_cents / 100;
  const sizePrice = size === 'large' ? basePrice + 2 : basePrice;
  const toppingsPrice = selectedToppings.length * 1.5;
  const totalPrice = (sizePrice + toppingsPrice) * quantity;

  const toggleTopping = (id: string) => {
    setSelectedToppings(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    // Get click position for animation
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setRewardPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });

    // Add to cart
    addItem({
      menuItemId: item.id,
      name: item.name,
      price: sizePrice,
      quantity,
      size,
      container,
      toppings: selectedToppings,
      imageUrl: item.image_url,
    });

    // Trigger reward animation
    setShowReward(true);

    // Bounce cart icon
    setCartBounce(true);
    setTimeout(() => setCartBounce(false), 600);

    // Show toast
    const randomMessage = funMessages[Math.floor(Math.random() * funMessages.length)];
    setToastMessage(randomMessage);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, [addItem, item, sizePrice, quantity, size, container, selectedToppings]);

  const handleRewardComplete = useCallback(() => {
    setShowReward(false);
  }, []);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        {/* Navigation arrows */}
        {hasPrev && (
          <button className={styles.navButtonLeft} onClick={goToPrev}>
            <ChevronLeft size={32} />
          </button>
        )}
        {hasNext && (
          <button className={styles.navButtonRight} onClick={goToNext}>
            <ChevronRight size={32} />
          </button>
        )}

        {/* Header with image - swipeable area */}
        <div
          className={styles.imageContainer}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            className={styles.closeButton}
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <X size={24} />
          </button>
          <button
            className={`${styles.cartButton} ${cartBounce ? styles.cartBounce : ''}`}
            onClick={() => navigate('/cart')}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <ShoppingCart size={24} />
            {cartTotal > 0 && (
              <span className={`${styles.cartBadge} ${cartBounce ? styles.badgePop : ''}`}>
                {cartTotal}
              </span>
            )}
          </button>
          <div className={styles.productImage}>
            <img
              src={getItemImage(item.id)}
              alt={item.name}
              className={styles.productImg}
              style={{
                transform: `translateX(${dragOffset}px) translateY(7px) scale(0.95)`,
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
            />
          </div>
          {/* Item counter */}
          <div className={styles.itemCounter}>
            {currentIndex + 1} / {items.length}
          </div>
          {/* Swipe hint on mobile */}
          {items.length > 1 && (
            <div className={styles.swipeHint}>Swipe to browse</div>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h1 className={styles.title}>{item.name}</h1>
          
          <div className={styles.rating}>
            <span className={styles.stars}>★★★★★</span>
            <span className={styles.reviews}>(127 reviews)</span>
          </div>

          <p className={styles.description}>{item.description}</p>

          {/* Size Selector */}
          <div className={styles.sizeSelector}>
            <button 
              className={`${styles.sizeOption} ${size === 'small' ? styles.sizeActive : ''}`}
              onClick={() => setSize('small')}
            >
              <span className={styles.sizeLabel}>SMALL</span>
              <span className={styles.sizePrice}>${basePrice.toFixed(2)}</span>
            </button>
            <button 
              className={`${styles.sizeOption} ${size === 'large' ? styles.sizeActive : ''}`}
              onClick={() => setSize('large')}
            >
              <span className={styles.sizeLabel}>LARGE</span>
              <span className={styles.sizePrice}>${(basePrice + 2).toFixed(2)}</span>
            </button>
          </div>

          {/* Toppings */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>ADD TOPPINGS (+$1.50 each)</h3>
            <div className={styles.toppingsList}>
              {TOPPINGS.map(topping => (
                <label key={topping.id} className={styles.toppingItem}>
                  <input
                    type="checkbox"
                    checked={selectedToppings.includes(topping.id)}
                    onChange={() => toggleTopping(topping.id)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxCustom}>
                    {selectedToppings.includes(topping.id) && '✓'}
                  </span>
                  <span>{topping.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Container */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>CONE OR CUP?</h3>
            <div className={styles.containerSelector}>
              <button 
                className={`${styles.containerOption} ${container === 'cone' ? styles.containerActive : ''}`}
                onClick={() => setContainer('cone')}
              >
                <span>🍦</span> Cone
              </button>
              <button 
                className={`${styles.containerOption} ${container === 'cup' ? styles.containerActive : ''}`}
                onClick={() => setContainer('cup')}
              >
                <span>🥤</span> Cup
              </button>
            </div>
          </div>
        </div>

        {/* Add to Cart Button */}
        <div className={styles.footer}>
          <button className={styles.addButton} onClick={handleAddToCart}>
            ADD TO CART - ${totalPrice.toFixed(2)}
          </button>
        </div>

        {/* Reward Animation */}
        <AddReward
          show={showReward}
          startPosition={rewardPosition}
          onComplete={handleRewardComplete}
        />

        {/* Toast */}
        <Toast message={toastMessage} show={showToast} />
      </div>
    </div>
  );
}
